/**
 * BridgeStrategy
 * Bridge 策略的抽象基礎類別
 * 只負責基礎通訊層，不包含業務邏輯
 */

import { EventEmitter } from '../utils/EventEmitter'
import type {
  BridgeMessage,
  ConnectionInfo,
  ConnectionStatus,
} from '../types/bridge.types'
import { BridgeAction } from '../types/bridge.types'
import type { PlatformType, WindowWithBridge } from '../types/platform.types'

export abstract class BridgeStrategy {
  protected win: WindowWithBridge
  protected eventEmitter: EventEmitter
  protected connectionStatus: ConnectionStatus = 'disconnected'
  protected lastActivity: Date | null = null
  protected messageIdCounter = 0
  protected pendingRequests: Map<
    string,
    {
      resolve: (value: unknown) => void
      reject: (error: unknown) => void
      timeout: number
    }
  > = new Map()

  constructor(eventEmitter: EventEmitter) {
    this.win = window as WindowWithBridge
    this.eventEmitter = eventEmitter
  }

  /**
   * 獲取平台類型
   */
  abstract getPlatform(): PlatformType

  /**
   * 初始化連接
   */
  abstract connect(): Promise<void>

  /**
   * 斷開連接
   */
  abstract disconnect(): void

  /**
   * 設置事件監聽器
   */
  setupListeners(): void {
    // do nothing
  }

  /**
   * 發送訊息到原生端（核心通訊方法）
   * @param action 動作名稱
   * @param data 資料
   * @returns Promise<T> 回應資料
   */
  abstract sendMessage<T>(action: BridgeAction, data?: unknown): Promise<T>

  /**
   * 獲取連接資訊
   */
  getConnectionInfo(): ConnectionInfo {
    return {
      status: this.connectionStatus,
      platform: this.getPlatform(),
      lastActivity: this.lastActivity?.toISOString(),
    }
  }

  /**
   * 生成唯一的訊息 ID
   */
  protected generateMessageId(): string {
    return `msg_${Date.now()}_${++this.messageIdCounter}`
  }

  /**
   * 創建 Bridge 訊息
   */
  protected createMessage(event: string, data?: unknown): BridgeMessage {
    return {
      event,
      data,
      timestamp: new Date().toISOString(),
      messageId: this.generateMessageId(),
    }
  }

  /**
   * 處理超時
   */
  protected createTimeout(timeout: number = 5000): Promise<never> {
    return new Promise((_, reject) => {
      window.setTimeout(() => {
        reject(new Error(`Request timeout after ${timeout}ms`))
      }, timeout)
    })
  }

  /**
   * 創建帶超時的 Promise
   */
  protected createPendingRequest<T>(
    messageId: string,
    timeout: number = 5000
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        this.pendingRequests.delete(messageId)
        reject(new Error(`Request timeout after ${timeout}ms`))
      }, timeout)

      this.pendingRequests.set(messageId, {
        resolve: resolve as (value: unknown) => void,
        reject: reject as (error: unknown) => void,
        timeout: timeoutId,
      })
    })
  }

  /**
   * 解析待處理的請求
   */
  protected resolvePendingRequest(messageId: string, data: unknown): void {
    const pending = this.pendingRequests.get(messageId)
    if (pending) {
      window.clearTimeout(pending.timeout)
      pending.resolve(data)
      this.pendingRequests.delete(messageId)
    }
  }

  /**
   * 拒絕待處理的請求
   */
  protected rejectPendingRequest(messageId: string, error: unknown): void {
    const pending = this.pendingRequests.get(messageId)
    if (pending) {
      window.clearTimeout(pending.timeout)
      pending.reject(error)
      this.pendingRequests.delete(messageId)
    }
  }

  /**
   * 更新連接狀態
   */
  protected updateConnectionStatus(status: ConnectionStatus): void {
    this.connectionStatus = status
    this.eventEmitter.emit('connectionStatusChanged', status)
  }

  /**
   * 更新最後活動時間
   */
  protected updateLastActivity(): void {
    this.lastActivity = new Date()
  }

  /**
   * 清理資源
   */
  protected cleanup(): void {
    // 清理所有待處理的請求
    this.pendingRequests.forEach(pending => {
      window.clearTimeout(pending.timeout)
      pending.reject(new Error('Connection closed'))
    })
    this.pendingRequests.clear()
  }

  /**
   * 處理接收到的訊息
   */
  protected handleMessage(data: unknown): void {
    try {
      // 類型檢查
      if (!data || typeof data !== 'object' || !('event' in data)) return
      
      const message = data as { event: string; messageId?: string; data?: unknown }
      
      if (!message.event) return

      // 處理回應訊息
      if (message.event.endsWith('Response')) {
        const messageId = message.messageId
        if (messageId && this.pendingRequests.has(messageId)) {
          this.resolvePendingRequest(messageId, message.data)
        }
      }

      // 發送事件
      this.eventEmitter.emit(message.event, message.data)
    } catch (error) {
      console.error('Error processing message:', error)
    }
  }

  /**
   * 呼叫 AsyncBridge API（統一實作）
   * 合併 Windows 和 Android 的優點
   */
  protected async callAsyncBridge(method: string, data?: unknown): Promise<unknown> {
    // 驗證 asyncBridge 存在
    if (!this.win.asyncBridge) {
      throw new Error(
        'AsyncBridge object not found. The WebView may not have injected it properly.'
      )
    }

    const asyncBridge = this.win.asyncBridge
    let fn = asyncBridge[method]

    // 處理可能的注入延遲（Android 特有）
    if (!fn) {
      console.warn(`AsyncBridge method ${method} not immediately available, waiting...`)
      await new Promise(resolve => setTimeout(resolve, 100))
      fn = asyncBridge[method]
    }

    if (!fn || typeof fn !== 'function') {
      const availableMethods = Object.keys(asyncBridge).filter(
        key => typeof asyncBridge[key] === 'function'
      )
      throw new Error(
        `AsyncBridge method ${method} not found. Available: ${availableMethods.join(', ')}`
      )
    }

    try {
      const bridgeFn = fn as (data?: string) => unknown
      const result = data 
        ? bridgeFn.call(asyncBridge, JSON.stringify(data)) 
        : bridgeFn.call(asyncBridge)

      // 處理各種返回類型
      if (result && typeof result === 'object') {
        const resultObj = result as Record<string, unknown>
        
        // Windows Proxy 物件特殊處理
        if (typeof resultObj._then === 'function') {
          return new Promise((resolve, reject) => {
            const thenFn = resultObj._then as (resolve: (value: unknown) => void, reject: (reason: unknown) => void) => void
            thenFn(resolve, reject)
          })
        }
        
        // 標準 Promise
        if ('then' in resultObj && typeof resultObj.then === 'function') {
          return await (result as Promise<unknown>)
        }
      }
      
      // 同步結果
      return result
    } catch (error) {
      const err = error as Error
      if (err.message && err.message.includes('non-injected object')) {
        throw new Error(
          `Bridge injection error: ${err.message}. Please ensure the WebView has properly injected the asyncBridge object.`
        )
      }
      console.error(`AsyncBridge ${method} error:`, err)
      throw error
    }
  }

  /**
   * Parse response data (generic method)
   * Trusts the agreed BridgeResponse<T> format between client and bridge
   */
  protected parseResponse<T>(result: unknown): T {
    // Handle string response
    if (typeof result === 'string') {
      try {
        const parsed = JSON.parse(result) as unknown
        // If has data field, return data
        if (parsed && typeof parsed === 'object' && 'data' in parsed) {
          return (parsed as { data: T }).data
        }
        return parsed as T
      } catch {
        // 如果不是 JSON，直接返回
        return result as T
      }
    }

    // Handle boolean
    if (typeof result === 'boolean') {
      return result as T
    }

    // Handle object
    if (result && typeof result === 'object') {
      if ('data' in result) {
        return (result as { data: T }).data
      }
      return result as T
    }

    return result as T
  }
}
